import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { type PbshoppingMenuItem } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import type { Filters } from './desktop-full-catalog'

interface StoreMenuProps {
  categories?: Array<PbshoppingMenuItem>
  categoryIndex: Filters['categoryIndex']
  onChangeFilters: (filters: Filters) => void
}

export const StoreMenu = ({
  categories,
  categoryIndex,
  onChangeFilters
}: StoreMenuProps) => {
  const { t } = useTranslation()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const theme = useAppSelector(
    (store) => store.settingsApi.settings.response.branding
  )

  const handleCategoryChange = (
    category: PbshoppingMenuItem,
    index: number
  ) => {
    posthog.capture('FC - Change category', {
      categoryName: category.title,
      categoryIndex: index,
      checkout_id: checkoutId
    })

    onChangeFilters({ categoryIndex: index, search: '' })
  }

  const isMisako = group === 'misako'
  const isHoff = group === 'hoff'

  if (isHoff) {
    return (
      <nav>
        <ul className="flex gap-1 overflow-x-auto md:flex-wrap md:gap-4">
          {categories?.map((category, index) => (
            <li
              key={category.resource_id ?? index}
              data-selected={index === categoryIndex}
              className="shrink-0 rounded-lg border-neutral-200 text-sm uppercase text-neutral-800 data-[selected=true]:bg-neutral-900 data-[selected=true]:text-white lg:text-base"
            >
              <button
                onClick={() => handleCategoryChange(category, index)}
                className="inline-block w-full px-3.5 py-2"
              >
                {!category?.title
                  ? t('full_catalog.all_products')
                  : category?.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  if (isMisako) {
    return (
      <nav>
        <ul className="flex gap-5 overflow-x-auto md:flex-wrap md:gap-8">
          {categories?.map((category, index) => (
            <li
              key={category.resource_id ?? index}
              data-selected={index === categoryIndex}
              className="shrink-0 rounded-md border-neutral-200 text-base text-neutral-900 underline-offset-[5px] hover:text-neutral-900 data-[selected=true]:underline"
            >
              <button
                onClick={() => handleCategoryChange(category, index)}
                className="text-sm font-light tracking-wider"
              >
                {!category?.title
                  ? t('full_catalog.all_products')
                  : category?.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  return (
    <aside className="relative w-full gap-6 overflow-y-auto border-solid border-neutral-900/10 py-2 md:flex md:max-h-full md:w-60 md:max-w-60 md:flex-col md:border-r md:bg-white md:pb-5 md:pt-6 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5">
      {theme?.logo_url ? (
        <div className="pointer-events-none hidden max-h-96 px-4 md:block">
          <div className="max-w-44 pl-1">
            <img
              src={theme?.logo_url}
              alt={theme?.title}
              width={176}
              height={40}
              className="h-full max-h-14 w-auto object-contain"
            />
          </div>
        </div>
      ) : null}
      <nav className="md:px-4">
        <ul className="flex gap-5 md:block md:space-y-1">
          {categories?.map((category, index) => (
            <li
              key={category.resource_id ?? index}
              data-selected={index === categoryIndex}
              className="shrink-0 rounded-md border-neutral-200 text-base font-medium text-neutral-500 underline-offset-[5px] hover:text-neutral-900 data-[selected=true]:text-neutral-900 data-[selected=true]:underline md:hover:bg-neutral-100 md:data-[selected=true]:bg-neutral-100 md:data-[selected=true]:no-underline"
            >
              <button
                onClick={() => handleCategoryChange(category, index)}
                className="w-full text-left font-medium md:px-3.5 md:py-2"
              >
                {!category?.title
                  ? t('full_catalog.all_products')
                  : category?.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  )
}
